/**
 * Date range utilities for API compatibility.
 * Ensures single-day or same-day ranges are sent as full UTC days so the backend returns data.
 */

/**
 * Returns true if two dates fall on the same UTC calendar day.
 */
function isSameUtcDay(a: Date, b: Date): boolean {
  const y1 = a.getUTCFullYear();
  const m1 = a.getUTCMonth();
  const d1 = a.getUTCDate();
  const y2 = b.getUTCFullYear();
  const m2 = b.getUTCMonth();
  const d2 = b.getUTCDate();
  return y1 === y2 && m1 === m2 && d1 === d2;
}

/**
 * Returns true if two dates fall on the same LOCAL calendar day.
 */
function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Start of a given date in UTC (00:00:00.000).
 */
function startOfUtcDay(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

/**
 * End of a given date in UTC (23:59:59.999).
 */
function endOfUtcDay(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(23, 59, 59, 999);
  return d;
}

/**
 * Start of the LOCAL calendar day (00:00:00.000 local time).
 *
 * Note: When serialized via `toISOString()`, JS will convert this local instant to UTC,
 * which is what APIs typically expect.
 */
export function startOfLocalDayInUtc(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
 * End of the LOCAL calendar day (23:59:59.999 local time).
 */
export function endOfLocalDayInUtc(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/**
 * Start of the LOCAL calendar month (first day 00:00:00).
 */
export function startOfLocalMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

/**
 * End of the LOCAL calendar month (last day 23:59:59.999).
 */
export function endOfLocalMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

/**
 * Start of week (Monday) in local time.
 */
export function startOfLocalWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * End of week (Sunday 23:59:59) in local time.
 */
export function endOfLocalWeek(date: Date): Date {
  const start = startOfLocalWeek(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Normalizes a date range for API requests. When start and end are the same UTC day
 * (or equal), returns that full day in UTC so the backend gets a non-zero-width range.
 * Otherwise returns the dates unchanged.
 */
export function normalizeDateRangeForApi(startDate: Date, endDate: Date): { startDate: Date; endDate: Date } {
  if (!startDate || !endDate) {
    return { startDate, endDate };
  }
  if (startDate.getTime() === endDate.getTime() || isSameUtcDay(startDate, endDate)) {
    return {
      startDate: startOfUtcDay(startDate),
      endDate: endOfUtcDay(endDate),
    };
  }
  return { startDate, endDate };
}

/**
 * Normalizes a date range selected in the UI (LOCAL calendar days) into UTC day boundaries
 * that preserve the user's intended dates when serialized via `toISOString()`.
 *
 * This avoids timezone offsets shifting the day in the backend.
 */
export function normalizeLocalDateRangeForApi(
  startDate: Date,
  endDate: Date
): { startDate: Date; endDate: Date } {
  if (!startDate || !endDate) {
    return { startDate, endDate };
  }

  if (startDate.getTime() === endDate.getTime() || isSameLocalDay(startDate, endDate)) {
    return {
      startDate: startOfLocalDayInUtc(startDate),
      endDate: endOfLocalDayInUtc(endDate),
    };
  }

  return {
    startDate: startOfLocalDayInUtc(startDate),
    endDate: endOfLocalDayInUtc(endDate),
  };
}
